import React, { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import clsx from 'clsx';
import { AutoSizer, List } from 'react-virtualized';
import styles from './VirtualizedDragAndDropList.module.css';

import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { makeStyles, Popover } from '@material-ui/core';

const VirtualizedDragAndDropList = ({
  classNames = {},
  enableAutoHeight,
  listHeight,
  rows,
  columns,
  rowHeight,
  shouldDisableDragForRow,
  shouldFixRow,
  onDragEnd,
}) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popoverVal, setPopoverVal] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handlePopoverOpen = (event, val, maxLength) => {
    if (event.currentTarget.scrollWidth > event.currentTarget.clientWidth) {
      setPopoverVal(val);
      setIsPopupOpen(true);
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = event => {
    setIsPopupOpen(false);
    setAnchorEl(null);
  };

  const closePopup = () => setIsPopupOpen(false);
  const usePopoverStyles = makeStyles(theme => ({
    popover: {
      pointerEvents: 'none',
    },
    paper: {
      padding: theme.spacing(1),
      color: 'white',
      backgroundColor: 'grey',
      fontSize: '11.8px', //TODO: keep same as table cell
    },
  }));

  const popoverClasses = usePopoverStyles();

  const rowRenderer = ({ style, index, key }) => {
    const row = rows[index];
    const renderTableRow = props => <TableRow {...props} />;
    if (shouldFixRow(row))
      return renderTableRow({
        row,
        index,
        style,
        key,
        classNames,
        columns,
        handlePopoverOpen,
        handlePopoverClose,
      });
    return (
      <Draggable
        draggableId={row.id}
        index={index}
        key={row.id}
        isDragDisabled={shouldDisableDragForRow?.(row)}
        style={style} // added to disble warnings of style not applied from react-virtualized
      >
        {provided =>
          renderTableRow({
            row,
            index,
            style,
            key,
            classNames,
            columns,
            provided,
            handlePopoverOpen,
            handlePopoverClose,
          })
        }
      </Draggable>
    );
  };

  const vListRef = useRef(null);
  const tableHeadRef = useRef(null);

  const handleListScroll = e =>
    (tableHeadRef.current.scrollLeft = e.target.scrollLeft);

  useEffect(() => {
    vListRef.current = document.getElementsByClassName(
      'ReactVirtualized__Grid ReactVirtualized__List',
    )[0];

    if (vListRef.current)
      vListRef.current.addEventListener('scroll', handleListScroll);

    return () => vListRef.current.removeEventListener('scroll', handleListScroll)
  }, []);

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <div
          style={{ width: '100%', height: '100%' }}
          className={classNames.tableContainerClassName}
        >
          <AutoSizer>
            {({ width: windowWidth, height: windowHeight }) => {
              let width = windowWidth;
              const autoHeight = windowHeight - rowHeight - 2;
              if (process.env.NODE_ENV === 'test') width = 2000; // to render in JSDOM
              return (
                <div
                  className={classNames.tableClassName}
                  style={{
                    height: enableAutoHeight ? autoHeight : '100%',
                    width: width,
                  }}
                >
                  <TableHead
                    columns={columns}
                    ref={tableHeadRef}
                    className={classNames.tableHeadClassName}
                  />
                  <Droppable
                    droppableId="droppable"
                    mode="virtual"
                    renderClone={(provided, snapshot, rubric) => (
                      <TableRow
                        row={rows[rubric.source.index]}
                        provided={provided}
                        classNames={classNames}
                        columns={columns}
                        snapshot={snapshot}
                      />
                    )}
                  >
                    {provided => {
                      return (
                        <List
                          containerStyle={{ overflow: 'initial' }}
                          rowRenderer={rowRenderer}
                          height={
                            enableAutoHeight ? autoHeight : listHeight - 50
                          }
                          rowHeight={rowHeight}
                          rowCount={rows.length}
                          width={width}
                          className={styles.listScroll}
                          overscanRowCount={5}
                          ref={ref => {
                            // taken from react-beautiful-dnd docs: they do this because there
                            // is no way to get the ref directly
                            if (ref) {
                              // eslint-disable-next-line react/no-find-dom-node
                              const listRef = findDOMNode(ref);
                              if (listRef instanceof HTMLElement) {
                                provided.innerRef(listRef);
                              }
                            }
                          }}
                        />
                      );
                    }}
                  </Droppable>
                </div>
              );
            }}
          </AutoSizer>
        </div>
      </DragDropContext>
      <Popover
        open={isPopupOpen}
        anchorEl={anchorEl}
        onClose={closePopup}
        className={popoverClasses.popover}
        classes={{
          paper: popoverClasses.paper,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {popoverVal}
      </Popover>
    </>
  );
};

const TableHead = forwardRef(({ columns, className }, ref) => {
  return (
    <div className={clsx(styles.tableHeader, className)} ref={ref}>
      {columns.map(col => (
        <span
          style={{
            ...col.style,
            ...col.headerStyles,
            display: 'inline-block',
            width: col.width,
          }}
        >
          {col.title}
        </span>
      ))}
    </div>
  );
});

const TableRow = ({
  classNames,
  columns,
  index,
  row,
  style,
  key,
  provided,
  snapshot,
  handlePopoverOpen,
  handlePopoverClose,
}) => {
  const backgroundColor = index % 2 === 0 ? '#f1f1f1' : 'white';

  return (
    <div
      key={key}
      className={clsx(
        styles.defaultRow,
        classNames.tableRowClassName instanceof Function
          ? classNames.tableRowClassName(row, snapshot?.isDragging)
          : classNames.tableRowClassName,
      )}
      {...provided?.draggableProps}
      {...provided?.dragHandleProps}
      ref={provided?.innerRef}
      style={{
        ...provided?.draggableProps?.style,
        ...style,
        backgroundColor,
        boxSizing: 'border-box',
      }}
    >
      {columns.map(col => {
        const renderColumnCell = () =>
          col.render && row ? col.render(row, index + 1) : row[col.field];

        return (
          <span
            className={clsx(classNames.tableCellClassName)}
            style={{
              ...col.style,
              ...col.cellStyles,
              display: 'inline-block',
              textOverflow: 'ellipsis',
              width: col.width,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
            key={`${row.asn}-${col.field}`}
            onMouseEnter={e =>
              handlePopoverOpen(e, row[col.field], col.maxLength)
            }
            onMouseLeave={e => handlePopoverClose(e)}
          >
            {renderColumnCell()}
          </span>
        );
      })}
    </div>
  );
};

VirtualizedDragAndDropList.propTypes = {
  classNames: PropTypes.shape({
    tableContainerClassName: PropTypes.string,
    tableClassName: PropTypes.string,
    tableRowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    tableHeadClassName: PropTypes.string,
  }),
  listHeight: PropTypes.number.isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired }))
    .isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      field: PropTypes.string,
      width: PropTypes.string.isRequired,
      headerStyles: PropTypes.object,
      style: PropTypes.object,
      cellStyles: PropTypes.object,
      render: PropTypes.func,
    }),
  ),
  rowHeight: PropTypes.number.isRequired,
  shouldDisableDragForRow: PropTypes.func,
  shouldFixRow: PropTypes.func,
  onDragEnd: PropTypes.func.isRequired,
};

export default VirtualizedDragAndDropList;
